import React, { useEffect, useRef, useState } from "react";
import { X, Link as LinkIcon, Loader2, CheckCircle2, AlertCircle, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * ImportSocialReelModal — paste a social URL → preview → import.
 *
 * Two-step UX
 * -----------
 *   Step 1  "paste"     — Creator drops a URL, we hit /api/social/preview
 *                         (yt-dlp metadata only, ~2-5s). Shows thumbnail,
 *                         title, author, duration, platform badge.
 *   Step 2  "confirm"   — Creator can edit the title/description, then
 *                         hits Import. We POST /api/social/import and
 *                         switch to a polling view that updates every 2s.
 *
 * Supported sources: TikTok, Instagram, YouTube, X/Twitter, Facebook, Vimeo.
 * Behind the scenes the import downloads the MP4, re-uploads it to
 * Cloudflare Stream, runs a Gemini thumbnail NSFW scan, and creates a
 * videos doc with source_platform=<key> so cards render the "via TikTok"
 * badge automatically.
 *
 * Closes itself on success and fires onImported({video_id, platform}) so
 * the parent can refresh its list / navigate to /watch/{id}.
 */
export default function ImportSocialReelModal({ open, onClose, onImported }) {
  const [step, setStep] = useState("paste"); // paste | confirm | importing | done | error
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");

  const pollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Reset on open so a subsequent import starts clean.
      setStep("paste"); setUrl(""); setPreview(null);
      setTitle(""); setDescription(""); setJobId(null);
      setJob(null); setErr(""); setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open]);

  const doPreview = async () => {
    const clean = (url || "").trim();
    if (!clean) return toast.error("Paste a social media URL first.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/social/preview", { url: clean });
      setPreview(data);
      setTitle(data.title || "");
      setDescription((data.description || "").slice(0, 800));
      setStep("confirm");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!preview) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/social/import", {
        url: preview.source_url,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
      });
      setJobId(data.job_id);
      setStep("importing");
      // Start polling every 2s.
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/social/import/${data.job_id}`);
          setJob(r.data);
          if (r.data.status === "done") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setStep("done");
            toast.success("Reel imported and published");
            onImported?.({ video_id: r.data.video_id, platform: r.data.platform });
          } else if (r.data.status === "error") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setErr(r.data.error || "Import failed");
            setStep("error");
          }
        } catch { /* keep polling on transient errors */ }
      }, 2000);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    onClose?.();
  };

  if (!open) return null;

  const platformColor = {
    tiktok:    "bg-pink-500",
    instagram: "bg-gradient-to-r from-amber-500 via-rose-500 to-fuchsia-500",
    youtube:   "bg-red-600",
    twitter:   "bg-neutral-800",
    facebook:  "bg-blue-600",
    vimeo:     "bg-sky-500",
  }[preview?.platform] || "bg-neutral-700";

  return (
    <div
      data-testid="import-social-modal"
      className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="w-full max-w-xl bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-gradient-to-r from-fuchsia-500/10 via-rose-500/10 to-amber-500/10">
          <div className="flex items-center gap-2">
            <LinkIcon className="w-4 h-4 text-tv-accent" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold">Import from Social</div>
          </div>
          <button data-testid="import-social-close" onClick={close} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {step === "paste" && (
            <div className="space-y-4">
              <h2 className="font-display font-black text-white text-2xl leading-tight">Paste a reel link</h2>
              <p className="text-white/60 text-sm">Drop a public URL from TikTok, Instagram, YouTube, X, Facebook, or Vimeo. We'll re-host it on Tap IN so it plays everywhere — including TVs that don't support social embeds.</p>
              <input
                ref={inputRef}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !busy) doPreview(); }}
                placeholder="https://www.tiktok.com/@you/video/…"
                data-testid="import-social-url"
                className="w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
                autoComplete="off"
              />
              {err && (
                <div className="flex items-start gap-2 text-rose-300 text-sm" data-testid="import-social-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button onClick={close} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-sm">Cancel</button>
                <button
                  data-testid="import-social-preview-btn"
                  onClick={doPreview}
                  disabled={busy || !url.trim()}
                  className="px-5 py-2 rounded-lg bg-tv-accent hover:bg-yellow-400 text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {busy ? "Reading link…" : "Preview"}
                </button>
              </div>
            </div>
          )}

          {step === "confirm" && preview && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {preview.thumbnail_url ? (
                  <img src={preview.thumbnail_url} alt="" className="w-32 aspect-[9/16] object-cover rounded-md bg-black" />
                ) : (
                  <div className="w-32 aspect-[9/16] rounded-md bg-black/60" />
                )}
                <div className="flex-1 min-w-0 space-y-1">
                  <span className={`inline-block text-[10px] font-black uppercase tracking-wider text-white px-2 py-0.5 rounded-full ${platformColor}`}>
                    via {preview.platform_label}
                  </span>
                  <div className="font-bold text-white truncate" title={preview.title}>{preview.title || "Untitled"}</div>
                  <div className="text-xs text-white/60 truncate">by {preview.author || "Unknown"}{preview.author_handle ? ` · ${preview.author_handle}` : ""}</div>
                  {preview.duration_sec != null && (
                    <div className="text-xs text-white/50">{Math.floor(preview.duration_sec/60)}:{String(preview.duration_sec%60).padStart(2,"0")}</div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Title on Tap IN</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={180}
                  data-testid="import-social-title"
                  className="mt-1 w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Description (optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  data-testid="import-social-desc"
                  className="mt-1 w-full bg-black/50 border border-white/15 focus:border-tv-accent outline-none rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              {err && (
                <div className="flex items-start gap-2 text-rose-300 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => setStep("paste")} className="text-sm text-white/50 hover:text-white/80">← Change URL</button>
                <div className="flex gap-2">
                  <button onClick={close} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-sm">Cancel</button>
                  <button
                    data-testid="import-social-confirm-btn"
                    onClick={doImport}
                    disabled={busy}
                    className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Import + Publish
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === "importing" && (
            <div className="space-y-4 py-4" data-testid="import-social-progress">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-tv-accent" />
                <div className="font-display font-bold text-white">{job?.phase || "Starting…"}</div>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-tv-accent transition-all duration-500" style={{ width: `${job?.progress_pct || 5}%` }} />
              </div>
              <p className="text-xs text-white/50">This can take up to a minute for longer clips — you can close this window and we'll finish in the background.</p>
            </div>
          )}

          {step === "done" && job && (
            <div className="space-y-4 py-2 text-center" data-testid="import-social-done">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
              <h2 className="font-display font-black text-2xl text-white">Published</h2>
              <p className="text-white/60 text-sm">
                {job.nsfw_rating === "explicit"
                  ? "Your reel was imported but held for admin review (NSFW flag)."
                  : "Your reel is live on Tap IN and will play across the platform and on TVs."}
              </p>
              <div className="flex items-center justify-center gap-2">
                <button onClick={close} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm">Close</button>
                {job.video_id && (
                  <a
                    href={`/watch/${job.video_id}`}
                    data-testid="import-social-watch-link"
                    className="px-5 py-2 rounded-lg bg-tv-accent hover:bg-yellow-400 text-black font-bold text-sm inline-flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" /> Watch now
                  </a>
                )}
              </div>
            </div>
          )}

          {step === "error" && (
            <div className="space-y-4 py-2 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-rose-400" />
              <h2 className="font-display font-black text-2xl text-white">Import failed</h2>
              <p className="text-white/60 text-sm">{err || "Something went wrong — please try a different URL."}</p>
              <div className="flex items-center justify-center gap-2">
                <button onClick={close} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm">Close</button>
                <button
                  onClick={() => { setStep("paste"); setErr(""); }}
                  className="px-5 py-2 rounded-lg bg-tv-accent text-black font-bold text-sm"
                >
                  Try another URL
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
